import {
  actorName,
  payloadText,
  type Sentence,
  type Translate,
} from "@/features/collaboration/lib/sentence";
import { dayHref } from "@/features/attendance/hooks/useDayParam";
import type { Schemas } from "@/shared/api/client";
import type { Locale } from "@/shared/i18n/messages";
import { formatDateTime, formatDay } from "@/shared/lib/format";

/* The people platform's notifications (requests, HR alerts, reviews, feedback and hiring) as sentences
   and the page each one opens. The task notifications live in sentences.ts. */

type Notification = Schemas["NotificationOut"];
type FlowSubject = Schemas["FlowSubject"];

const SUBJECTS: readonly FlowSubject[] = [
  "attendance_request",
  "leave_request",
  "overtime",
  "expense_claim",
  "letter_request",
];
const ALERTS = [
  "contract_ending",
  "probation_ending",
  "document_expiring",
  "certification_expiring",
  "asset_not_returned",
  "break_overrun",
] as const;
type Alert = (typeof ALERTS)[number];
const FEEDBACK_KINDS = ["praise", "private", "requested"] as const;
type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

function oneOf<T extends string>(values: readonly T[], value: unknown): T | null {
  return typeof value === "string" && (values as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function payloadNumber(payload: Record<string, unknown>, key: string): number {
  const value = payload[key];
  return typeof value === "number" ? value : 0;
}

/** An ISO date from a payload in the reader's words, or "" when absent. */
function payloadDate(payload: Record<string, unknown>, key: string, locale: Locale): string {
  const value = payloadText(payload, key);
  return value ? formatDay(value.slice(0, 10), locale) : "";
}

function requestSentence(notification: Notification, translate: Translate): Sentence {
  const payload = notification.payload;
  const subject = oneOf(SUBJECTS, payload.subject_type) ?? "attendance_request";
  if (notification.kind === "request_pending") {
    return {
      id: "requests.notify.pending",
      values: {
        actor: actorName(notification.actor, translate),
        target: translate(`requests.notify.subject.${subject}`),
      },
      targetKind: "phrase",
    };
  }
  return {
    id: "requests.notify.decided",
    values: {
      status: payloadText(payload, "status") ?? "",
      target: translate(`requests.notify.yours.${subject}`),
    },
    targetKind: "phrase",
  };
}

/** A whole number a payload sends as text ("25"), or 0. */
function payloadCount(payload: Record<string, unknown>, key: string): number {
  const value = payload[key];
  const number = typeof value === "number" ? value : Number(payloadText(payload, key));
  return Number.isFinite(number) ? number : 0;
}

function alertSentence(notification: Notification, locale: Locale): Sentence {
  const payload = notification.payload;
  const alert: Alert = oneOf(ALERTS, payload.alert) ?? "contract_ending";
  if (alert === "break_overrun") {
    // The label carries the minutes over the allowance.
    return {
      id: "requests.notify.alert.break_overrun",
      values: {
        name: payloadText(payload, "employee_name") ?? "",
        minutes: payloadCount(payload, "label"),
        date: payloadDate(payload, "on", locale),
      },
      targetKind: "name",
    };
  }
  return {
    id: `requests.notify.alert.${alert}`,
    values: {
      name: payloadText(payload, "employee_name") ?? "",
      target: payloadText(payload, "label") ?? "",
      date: payloadDate(payload, "on", locale),
      days: payloadNumber(payload, "days"),
    },
    targetKind: "name",
  };
}

function growthSentence(
  notification: Notification,
  translate: Translate,
  locale: Locale,
): Sentence {
  const payload = notification.payload;
  const date = payloadDate(
    payload,
    notification.kind === "feedback_requested" ? "due_on" : "due",
    locale,
  );
  switch (notification.kind) {
    case "review_requested":
      return {
        id:
          payload.reminder === true
            ? "requests.notify.reviewReminder"
            : "requests.notify.reviewRequested",
        values: { target: payloadText(payload, "cycle_name") ?? "", date },
        targetKind: "name",
      };
    case "review_published":
      return {
        id: "requests.notify.reviewPublished",
        values: { target: payloadText(payload, "cycle_name") ?? "" },
        targetKind: "name",
      };
    case "feedback_received": {
      const kind: FeedbackKind = oneOf(FEEDBACK_KINDS, payload.kind) ?? "praise";
      const author =
        payloadText(payload, "author_name") ?? actorName(notification.actor, translate);
      return {
        id: `requests.notify.feedback.${kind}`,
        values: { actor: author },
        targetKind: "phrase",
      };
    }
    default:
      return {
        id: date ? "requests.notify.feedbackRequestedBy" : "requests.notify.feedbackRequested",
        values: {
          actor: actorName(notification.actor, translate),
          target: payloadText(payload, "subject_name") ?? "",
          date,
        },
        targetKind: "name",
      };
  }
}

/** A hiring notification: who applied, an interview I sit on, an offer answered. */
function hiringSentence(notification: Notification, locale: Locale): Sentence {
  const payload = notification.payload;
  const opening =
    (locale === "ar" ? payloadText(payload, "opening_title_ar") : null) ??
    payloadText(payload, "opening_title") ??
    "";
  const values = { name: payloadText(payload, "candidate_name") ?? "", target: opening };
  if (notification.kind === "application_received") {
    return { id: "requests.notify.applicationReceived", values, targetKind: "name" };
  }
  if (notification.kind === "interview_scheduled") {
    const when = payloadText(payload, "scheduled_at");
    return {
      id: "requests.notify.interviewScheduled",
      values: { ...values, date: when ? formatDateTime(when, locale) : "" },
      targetKind: "name",
    };
  }
  return {
    id:
      payload.accepted === true ? "requests.notify.offerAccepted" : "requests.notify.offerDeclined",
    values,
    targetKind: "name",
  };
}

/** A people-platform notification as a sentence; null for every other kind. */
export function peopleSentence(
  notification: Notification,
  translate: Translate,
  locale: Locale,
): Sentence | null {
  switch (notification.kind) {
    case "request_pending":
    case "request_decided":
      return requestSentence(notification, translate);
    case "hr_alert":
      return alertSentence(notification, locale);
    case "review_requested":
    case "review_published":
    case "feedback_received":
    case "feedback_requested":
      return growthSentence(notification, translate, locale);
    case "application_received":
    case "interview_scheduled":
    case "offer_answered":
      return hiringSentence(notification, locale);
    default:
      return null;
  }
}

/* Where a decided request is followed up: the requester's own page for that kind of request. */
const DECIDED_PAGES: Readonly<Record<FlowSubject, string>> = {
  attendance_request: "/me/hr/attendance",
  leave_request: "/me/hr/time-off",
  overtime: "/me/hr/time-off",
  expense_claim: "/me/hr/pay",
  letter_request: "/me/hr/letters",
};

export interface LinkContext {
  /** Whether the reader may open people's records (hr.view). */
  canViewPeople: boolean;
  /** The signed-in person, so an alert about oneself opens one's own HR page. */
  userId: string | null;
}

/** The page a people-platform notification opens; null for other kinds (or nowhere to go). */
export function peopleLink(notification: Notification, context: LinkContext): string | null {
  const payload = notification.payload;
  switch (notification.kind) {
    case "request_pending":
      return "/requests";
    case "request_decided":
      return DECIDED_PAGES[oneOf(SUBJECTS, payload.subject_type) ?? "attendance_request"];
    case "hr_alert": {
      const employeeId = payloadText(payload, "employee_id");
      const day = payloadText(payload, "on")?.slice(0, 10);
      // A break overrun opens that day in the attendance grid, like a review item does.
      if (payload.alert === "break_overrun" && employeeId && day && context.canViewPeople) {
        return dayHref({ employeeId, date: day });
      }
      if (employeeId && context.canViewPeople) return `/people/${employeeId}`;
      return employeeId !== null && employeeId === context.userId ? "/me/hr" : null;
    }
    case "review_requested":
    case "review_published":
      return "/me/hr/growth/reviews";
    case "feedback_received":
    case "feedback_requested":
      return "/me/hr/growth/feedback";
    case "application_received":
    case "interview_scheduled":
    case "offer_answered": {
      const applicationId = payloadText(payload, "application_id");
      return applicationId ? `/hiring/applications/${applicationId}` : null;
    }
    default:
      return null;
  }
}
